import {useContext} from 'react'
import type {LoaderFunctionArgs} from 'react-router'
import {redirect, useLoaderData} from 'react-router-dom'

import Context from '@s-ui/react-context'

import {getNavigationState} from '../../helpers/navigationStateCache.ts'
import {withThirdPartiesParam} from '../../hocs/withThirdPartiesParam.js'
import VehicleSuggesterResults from './Results/index.js'
import {getInitialProps} from './getInitialProps'
import VehicleSuggesterComponentRaw from './index.js'

// Import styles
import './styles/index.scss'
import './Results/styles/index.scss'
// HOC wrapping that used to live in `./index.js`. Moved here so `./index.js`
// exports the raw component and receives Fast Refresh in dev.
const VehicleSuggesterComponent = withThirdPartiesParam(VehicleSuggesterComponentRaw)

export async function loader({context, request}: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const pathname = url.pathname
  const query = Object.fromEntries(url.searchParams.entries())

  // Recover the state the previous step pushed (see helpers/navigationStateCache.ts for why
  // a RR7 loader can't read `location.state`). Steps after the first read priceRange from
  // here; without it they throw and handleError bounces the user back to step 1.
  const state = getNavigationState(pathname)

  // Build routeInfo compatible with getInitialProps
  const routeInfo = {
    location: {
      pathname,
      query,
      search: url.search,
      state
    },
    params: {}
  }

  // Call existing getInitialProps
  const data = await getInitialProps({routeInfo, context})

  // `handleError` signals "bounce back to step 1" with the legacy `__HTTP__` convention,
  // which @s-ui/react-initial-props used to turn into a server redirect. Nothing reads it
  // under RR7, so it reached the component as a prop and `index.js` called router.push()
  // during render — React warns and the redirect never lands. Hitting a deep step directly
  // (no wizard state, so the price filter is missing) takes exactly this path.
  const redirectTo: unknown = data?.__HTTP__?.redirectTo
  if (typeof redirectTo === 'string' && redirectTo !== '') {
    return redirect(redirectTo)
  }

  return data
}

export function Component() {
  const data = useLoaderData()
  const {domain} = useContext(Context)

  // All six suggester paths share this route module, but the results page is its own
  // component (own layout, vehicle cards and overview drawer) — `./index.js` only knows
  // how to render the five filter steps. Dispatch on pageName, the same way
  // getInitialProps picks which step's props builder to run.
  const {VEHICLE_SUGGESTER_RESULTS_PAGE_NAME} = domain.get('config').get('page')
  if (data?.pageName === VEHICLE_SUGGESTER_RESULTS_PAGE_NAME) {
    return <VehicleSuggesterResults {...data} />
  }

  return <VehicleSuggesterComponent {...data} />
}
